import type { ScanCapture } from "../../lib/scan/useScanCapture";
import { CornerEditor } from "./CornerEditor";

interface Props {
  scan: ScanCapture;
  /** Label of the button that finishes the review step. */
  acceptLabel: string;
  onAccept: () => void;
  /** Called after a rotate, e.g. to drop tags placed on the old orientation. */
  onRotated?: () => void;
}

/**
 * The corners and review steps of scanning a print. The camera step is
 * full screen (CameraCapture), so callers render that themselves.
 */
export function ScanCaptureSteps({ scan, acceptLabel, onAccept, onRotated }: Props) {
  const { step, frameUrl, shownUrl, working } = scan;

  if (step === "corners" && frameUrl) {
    return (
      <section className="stack">
        <p className="text-muted" style={{ margin: 0 }}>
          {scan.detected
            ? "Check the corners sit on the edges of the photo. Drag any that are off."
            : "Couldn't find the edges by itself. Drag the four corners onto the photo's edges."}
        </p>
        <CornerEditor src={frameUrl} quad={scan.quad} onChange={scan.setQuad} />
        <div className="scan-actions">
          <button type="button" className="btn-tertiary" onClick={scan.reset} disabled={Boolean(working)}>
            Retake
          </button>
          <button type="button" onClick={() => void scan.straighten()} disabled={Boolean(working)}>
            {working ?? "Straighten"}
          </button>
        </div>
      </section>
    );
  }

  if (step === "review" && shownUrl) {
    return (
      <section className="stack">
        <img className="scan-preview" src={shownUrl} alt="Scanned photo" />
        <div className="scan-toggle" role="group" aria-label="Enhancement">
          <button type="button" className={scan.useEnhanced ? "" : "btn-secondary"} onClick={() => scan.setUseEnhanced(true)}>
            Enhanced
          </button>
          <button type="button" className={scan.useEnhanced ? "btn-secondary" : ""} onClick={() => scan.setUseEnhanced(false)}>
            Original
          </button>
        </div>
        <div className="scan-actions">
          <button type="button" className="btn-tertiary" onClick={() => scan.setStep("corners")} disabled={Boolean(working)}>
            ← Corners
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void scan.rotate().then(() => onRotated?.())}
            disabled={Boolean(working)}
          >
            ↻ Rotate
          </button>
          <button type="button" onClick={onAccept} disabled={Boolean(working)}>
            {acceptLabel}
          </button>
        </div>
      </section>
    );
  }

  return null;
}
